import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { MdDeleteOutline } from "react-icons/md";

import api from "../../../services/api";
import toast from "react-hot-toast";

const Activities = ({ invoice, setInvoice }) => {
  const [activities, setActivities] = useState([]);
  const [banks, setBanks] = useState([]);
  const [epics, setEpics] = useState([]);
  const [budgetInvoices, setBudgetInvoices] = useState([]);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const queryActivities = { budgetId: invoice.budgetId };
        const resActivities = await api.post("/activity/search", queryActivities);
        if (resActivities.ok) setActivities(resActivities.data.map((e) => ({ ...e, type: "activity" })));

        const queryBanks = { budgetId: invoice.budgetId };
        const resBanks = await api.post("/bank/search", queryBanks);
        if (resBanks.ok) setBanks(resBanks.data.banks.map((e) => ({ ...e, type: "bank" })));

        const queryEpics = { budget_id: invoice.budgetId };
        const resEpics = await api.post("/epic/search", queryEpics);
        if (resEpics.ok) setEpics(resEpics.data.map((e) => ({ ...e, type: "epic" })));

        const queryInvoices = { budgetId: invoice.budgetId };
        const resInvoices = await api.post("/invoice/search", queryInvoices);
        if (resInvoices.ok) setBudgetInvoices(resInvoices.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);

  // Map: bankId → invoice it's attached to (for graying out banks already on another invoice)
  const bankAttachedInvoice = useMemo(() => {
    const map = new Map();
    for (const inv of budgetInvoices) {
      for (const item of inv.itemsCompta || []) {
        if (item.category === "expense" && item.bankId) {
          map.set(String(item.bankId), { invoiceId: inv._id, invoiceName: inv.name });
        }
      }
    }
    return map;
  }, [budgetInvoices]);

  const isBankOnCurrentInvoice = (bank) => {
    const att = bankAttachedInvoice.get(String(bank._id));
    return att && att.invoiceId === invoice._id;
  };

  const isBankOnOtherInvoice = (bank) => {
    const att = bankAttachedInvoice.get(String(bank._id));
    return att && att.invoiceId !== invoice._id;
  };

  const otherInvoiceForBank = (bank) => {
    const att = bankAttachedInvoice.get(String(bank._id));
    return att && att.invoiceId !== invoice._id ? att : null;
  };

  const refreshInvoice = async () => {
    const { ok, data } = await api.get(`/invoice/${invoice._id}?getLeadCashout=true`);
    if (ok) setInvoice(data);
  };

  const handleAttachBanksToInvoice = async (bankIds) => {
    if (!bankIds.length) return;
    const newEntries = bankIds.map((bankId) => {
      const bank = banks.find((b) => b._id === bankId);
      const cost = Math.abs(bank.amount);
      return {
        name: bank.name,
        bankId: bank._id,
        description: bank.description || "",
        cost,
        price: cost,
        quantity: 1,
        unit: "",
        totalCost: cost,
        totalPrice: cost,
        category: "expense",
      };
    });
    const existing = invoice.itemsCompta || [];
    const filtered = existing.filter((it) => !(it.category === "expense" && bankIds.includes(String(it.bankId))));
    const itemsCompta = [...filtered, ...newEntries];
    const res = await api.put(`/invoice/${invoice._id}`, { itemsCompta });
    if (res.ok) {
      setInvoice(res.data);
      // Refresh budgetInvoices map so other-invoice graying stays correct
      setBudgetInvoices((prev) => prev.map((i) => (i._id === invoice._id ? res.data : i)));
    }
  };

  const handleDetachBankFromInvoice = async (bankId) => {
    const itemsCompta = (invoice.itemsCompta || []).filter((it) => !(it.category === "expense" && String(it.bankId) === String(bankId)));
    const res = await api.put(`/invoice/${invoice._id}`, { itemsCompta });
    if (res.ok) {
      setInvoice(res.data);
      setBudgetInvoices((prev) => prev.map((i) => (i._id === invoice._id ? res.data : i)));
    }
  };

  const handleChangeMany = async () => {
    try {
      const update = { invoiceId: invoice._id, invoiceName: invoice.name };

      const activityIds = selected.filter((a) => a.type === "activity").map((a) => a._id);
      const bankIds = selected.filter((a) => a.type === "bank").map((a) => a._id);
      const epicIds = selected.filter((a) => a.type === "epic").map((a) => a._id);

      if (activityIds.length) {
        const resActivities = await api.put(`/activity/many`, { ids: activityIds, update });
        if (resActivities.ok) setActivities(activities.map((a) => (activityIds.includes(a._id) ? { ...a, ...update } : a)));
      }
      if (bankIds.length) {
        await handleAttachBanksToInvoice(bankIds);
      }
      if (epicIds.length) {
        for (const epicId of epicIds) {
          const res = await api.put(`/epic/${epicId}`, update);
          if (res.ok) setEpics(epics.map((e) => (e._id === epicId ? { ...e, ...update } : e)));
        }
      }

      setSelected([]);
      toast.success("updated");
    } catch (error) {
      console.error(error);
      toast.error("error");
    }
  };

  const handleRemoveActivity = async (activity) => {
    try {
      const update = { invoiceId: null, invoiceName: null };
      const res = await api.put(`/activity/${activity._id}`, update);
      if (res.ok) {
        setActivities(activities.map((a) => (a._id === activity._id ? { ...a, ...update } : a)));
        toast.success("updated");
      }
    } catch (error) {
      console.error(error);
      toast.error("error");
    }
  };

  const activitiesTotalCost = activities.reduce((acc, a) => acc + a.cost, 0);
  const activitiesTotalPrice = activities.reduce((acc, a) => acc + a.value, 0);
  const activitesMargin = (activitiesTotalPrice - activitiesTotalCost) / activitiesTotalCost;

  const attachedActivities = activities.filter((a) => a.invoiceId === invoice._id);
  const expenseItems = (invoice.itemsCompta || []).filter((it) => it.category === "expense");

  const invoiceTotalCost =
    attachedActivities.reduce((acc, a) => acc + (a.cost || 0), 0) +
    expenseItems.reduce((acc, e) => acc + (e.totalCost || 0), 0);
  const invoiceTotalPrice =
    attachedActivities.reduce((acc, a) => acc + (a.value || 0), 0) +
    expenseItems.reduce((acc, e) => acc + (e.totalPrice || 0), 0);
  const invoiceMargin = invoiceTotalCost ? (invoiceTotalPrice - invoiceTotalCost) / invoiceTotalCost : 0;

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h2 className="text-gray-800">Associated to this invoice</h2>
        <div className="w-full p-6 border border-gray-300 rounded">
          <table className="table-fixed">
            <thead className="border-b border-gray-300">
              <tr>
                <th colSpan={3} className="text-left font-semibold text-sm p-2">
                  Name
                </th>
                <th className="text-right font-semibold text-sm px-3">Cost</th>
                <th className="text-right font-semibold text-sm px-3">Price</th>
                <th className="text-right font-semibold text-sm px-3">Quantity</th>
                <th className="text-right font-semibold text-sm px-3">Total Cost</th>
                <th className="text-right font-semibold text-sm px-3 pr-10">Total Price</th>
              </tr>
            </thead>
            <tbody>
              {attachedActivities.length > 0 || expenseItems.length > 0 ? (
                <>
                  {expenseItems.map((item, index) => (
                    <BankRowInvoice key={`bank-${index}`} item={item} onRemove={() => handleDetachBankFromInvoice(item.bankId)} />
                  ))}
                  {attachedActivities.map((item, index) => (
                    <ActivityRowInvoice key={`act-${index}`} data={item} onRemove={() => handleRemoveActivity(item)} />
                  ))}
                </>
              ) : (
                <tr>
                  <td colSpan={7} className="p-2 text-sm text-center text-gray-400">
                    No data linked to the invoice
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="border-t border-gray-300">
              <tr>
                <td colSpan={6} className="p-2 text-sm font-semibold">
                  Total
                </td>
                <td className="text-right p-2 text-sm font-semibold">{invoiceTotalCost.toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
                <td className="text-right p-2 text-sm font-semibold">
                  <span className={`p-1 ${invoiceMargin < 0 ? "bg-red-200" : "bg-green-200"} rounded text-[10px] mr-2`}>
                    {invoiceMargin < 0 ? "-" : "+"}
                    {invoiceMargin.toLocaleString("fr", { style: "percent" })}
                  </span>
                  {invoiceTotalPrice.toLocaleString("fr", { style: "currency", currency: "EUR" })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between items-center gap-2">
          <h2 className="text-gray-800">Activities of the budget</h2>
          {selected.length > 0 && (
            <div className="flex items-center gap-3">
              <button className="blue-btn" onClick={() => handleChangeMany()}>
                Add to Invoice
              </button>
            </div>
          )}
        </div>
        <div className="w-full  p-6 border border-gray-300 rounded">
          <table className="table-fixed">
            <thead className="border-b border-gray-300">
              <tr>
                <th colSpan={3} className="text-left font-semibold text-sm p-2">
                  Name
                </th>
                <th className="text-right font-semibold text-sm px-3">Cost</th>
                <th className="text-right font-semibold text-sm px-3">Price</th>
                <th className="text-right font-semibold text-sm px-3">Quantity</th>
                <th className="text-right font-semibold text-sm px-3">Total Cost</th>
                <th className="text-right font-semibold text-sm px-3">Total Price</th>
              </tr>
            </thead>
            <tbody>
              {activities.length > 0 ? (
                activities.map((item, index) => (
                  <tr key={index} className={`${item.invoiceId ? (item.invoiceId === invoice._id ? "bg-sky-100 text-sky-600" : "bg-gray-100 text-gray-600") : ""}`}>
                    <td colSpan={3} className="p-2 text-sm">
                      <div className="flex items-center gap-2">
                        {item.invoiceId === invoice._id ? (
                          <input type="checkbox" id={`checkbox-${item._id}`} checked className="rounded mr-2 text-sky-700" disabled />
                        ) : (
                          <input
                            type="checkbox"
                            id={`checkbox-${item._id}`}
                            className="rounded mr-2 text-sky-700"
                            checked={selected.some((s) => s._id === item._id)}
                            onChange={(e) => setSelected(e.target.checked ? [...selected, item] : selected.filter((s) => s._id !== item._id))}
                          />
                        )}
                        <label htmlFor={`checkbox-${item._id}`} className="truncate">
                          <p className="flex items-center">
                            {item.userName}
                            {item.invoiceId ? (
                              <p>
                                - <span className="border border-gray-600 text-gray-600 text-xs px-1 rounded truncate">{item.invoiceName}</span>
                              </p>
                            ) : null}
                          </p>
                          <p className="text-xs text-gray-500">{new Date(item.date).toLocaleDateString("en-US", { year: "numeric", month: "2-digit" })}</p>
                        </label>
                      </div>
                    </td>
                    <td className="text-right p-2 text-sm">{(item.userTjm || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
                    <td className="text-right p-2 text-sm">{(item.userTjms || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
                    <td className="text-right p-2 text-sm">{(item.total / 8).toFixed(2)} days</td>
                    <td className="text-right p-2 text-sm">{(item.cost || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
                    <td className="text-right p-2 text-sm">{(item.value || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="p-2 text-sm text-center text-gray-400">
                    No activity found
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="border-t border-gray-300">
              <tr>
                <td colSpan={3} className="p-2 text-sm font-semibold">
                  Total
                </td>
                <td className="text-right p-2 text-sm font-semibold">
                  {activities.reduce((acc, a) => acc + a.userTjm, 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}
                </td>
                <td className="text-right p-2 text-sm font-semibold">
                  {activities.reduce((acc, a) => acc + a.userTjms, 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}
                </td>
                <td className="text-right p-2 text-sm font-semibold">{activities.reduce((acc, a) => acc + a.total / 8, 0).toFixed(2)} days</td>

                <td className="text-right p-2 text-sm font-semibold">{activitiesTotalCost.toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
                <td className="text-right p-2 text-sm font-semibold">
                  <span className={`p-1 ${activitesMargin < 0 ? "bg-red-200" : "bg-green-200"} rounded text-[10px] mr-2`}>
                    {activitesMargin < 0 ? "-" : "+"}
                    {activitesMargin.toLocaleString("fr", { style: "percent" })}
                  </span>
                  {activitiesTotalPrice.toLocaleString("fr", { style: "currency", currency: "EUR" })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between items-center gap-2">
          <h2 className="text-gray-800">Banks of the budget</h2>
          {selected.length > 0 && (
            <div className="flex items-center gap-3">
              <button className="blue-btn" onClick={() => handleChangeMany()}>
                Add to Invoice
              </button>
            </div>
          )}
        </div>
        <div className="w-full p-6 border border-gray-300 rounded">
          <table className="table-fixed">
            <thead className="border-b border-gray-300">
              <tr>
                <th colSpan={2} className="text-left font-semibold text-sm p-2">
                  Item
                </th>
                <th className="text-left font-semibold text-sm p-2">Date</th>
                <th className="text-right font-semibold text-sm p-2">Amount</th>
                <th className="text-right font-semibold text-sm p-2">Category</th>
              </tr>
            </thead>
            <tbody>
              {banks.length > 0 ? (
                banks.map((item, index) => {
                  const onCurrent = isBankOnCurrentInvoice(item);
                  const onOther = isBankOnOtherInvoice(item);
                  const otherInv = otherInvoiceForBank(item);
                  return (
                    <tr key={index} className={onCurrent ? "bg-sky-100 text-sky-600" : onOther ? "bg-gray-100 text-gray-600" : ""}>
                      <td colSpan={2} className="text-right p-2 text-sm">
                        <div className="flex items-center gap-2">
                          {onCurrent ? (
                            <input type="checkbox" id={`checkbox-${item._id}`} checked className="rounded mr-2 text-sky-700" disabled />
                          ) : (
                            <input
                              type="checkbox"
                              id={`checkbox-${item._id}`}
                              className="rounded mr-2 text-sky-700"
                              checked={selected.some((s) => s._id === item._id)}
                              onChange={(e) => setSelected(e.target.checked ? [...selected, item] : selected.filter((s) => s._id !== item._id))}
                            />
                          )}

                          <Link to={`/bank/${item._id}`}>
                            <label htmlFor={`checkbox-${item._id}`} className="truncate">
                              {item.name}
                              {otherInv ? <span className="ml-2 border border-gray-600 text-gray-600 text-xs p-1 rounded truncate">{otherInv.invoiceName}</span> : null}
                            </label>
                          </Link>
                        </div>
                      </td>
                      <td className="p-2 text-sm">{new Date(item.date).toLocaleDateString()}</td>
                      <td className="text-right p-2 text-sm">{(-item.amount).toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
                      <td className="text-right p-2 text-sm">{item.category}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="p-2 text-sm text-center text-gray-400">
                    No bank found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between items-center gap-2">
          <h2 className="text-gray-800">Epics of the budget</h2>
          {selected.length > 0 && (
            <div className="flex items-center gap-3">
              <button className="blue-btn" onClick={() => handleChangeMany()}>
                Add to Invoice
              </button>
            </div>
          )}
        </div>
        <div className="w-full p-6 border border-gray-300 rounded">
          <table className="table-fixed">
            <thead className="border-b border-gray-300">
              <tr>
                <th colSpan={2} className="text-left font-semibold text-sm p-2">
                  Title
                </th>
                <th className="text-left font-semibold text-sm p-2">Status</th>
                <th className="text-right font-semibold text-sm p-2">Days Required</th>
                <th className="text-right font-semibold text-sm p-2">Cost</th>
              </tr>
            </thead>
            <tbody>
              {epics.length > 0 ? (
                epics.map((item, index) => (
                  <tr key={index} className={`${item.invoice_id ? (item.invoice_id === invoice._id ? "bg-sky-100 text-sky-600" : "bg-gray-100 text-gray-600") : ""}`}>
                    <td colSpan={2} className="p-2 text-sm">
                      <div className="flex items-center gap-2">
                        {item.invoice_id === invoice._id ? (
                          <input type="checkbox" id={`checkbox-${item._id}`} checked className="rounded mr-2 text-sky-700" disabled />
                        ) : (
                          <input
                            type="checkbox"
                            id={`checkbox-${item._id}`}
                            className="rounded mr-2 text-sky-700"
                            checked={selected.some((s) => s._id === item._id)}
                            onChange={(e) => setSelected(e.target.checked ? [...selected, item] : selected.filter((s) => s._id !== item._id))}
                          />
                        )}
                        <label htmlFor={`checkbox-${item._id}`} className="truncate">
                          {item.title}
                          {item.invoice_id ? <span className="ml-2 border border-gray-600 text-gray-600 text-xs px-1 rounded truncate">{item.invoice_name}</span> : null}
                        </label>
                      </div>
                    </td>
                    <td className="p-2 text-sm">{item.status}</td>
                    <td className="text-right p-2 text-sm">{item.days_required || 0}</td>
                    <td className="text-right p-2 text-sm">{((item.days_required || 0) * (item.tjm || 0)).toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="p-2 text-sm text-center text-gray-400">
                    No epics found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const BankRowInvoice = ({ item, onRemove }) => (
  <tr>
    <td colSpan={3} className="p-2 text-sm">
      <div className="flex items-center gap-2">
        <span className="border border-sky-700 text-sky-700 text-xs p-1 rounded">Bank</span>
        <p className="font-semibold">{item.name}</p>
      </div>
    </td>
    <td className="text-right p-2 text-sm">{(item.cost || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
    <td className="text-right p-2 text-sm">{(item.price || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
    <td className="text-right p-2 text-sm">{item.quantity || 1}</td>
    <td className="text-right p-2 text-sm">{(item.totalCost || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
    <td className="text-right p-2 text-sm">
      <div className="w-full flex items-center">
        <p className="flex-1">{(item.totalPrice || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</p>
        <button className="text-sm h-10 w-10 flex items-center justify-center text-red-600" onClick={onRemove}>
          <MdDeleteOutline />
        </button>
      </div>
    </td>
  </tr>
);

const ActivityRowInvoice = ({ data, onRemove }) => (
  <tr>
    <td colSpan={3} className="p-2 text-sm">
      <div className="flex items-center gap-2">
        <span className="border border-yellow-600 text-yellow-600 text-xs p-1 rounded">Activity</span>

        <p className="font-semibold">{data.userName}</p>
      </div>
    </td>
    <td className="text-right p-2 text-sm">{(data.userTjm || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
    <td className="text-right p-2 text-sm">{(data.userTjms || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
    <td className="text-right p-2 text-sm">{(data.total / 8).toFixed(2)} days</td>
    <td className="text-right p-2 text-sm">{(data.cost || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
    <td className="text-right p-2 text-sm">
      <div className="w-full flex items-center">
        <p className="flex-1">{(data.value || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</p>

        <button className="text-sm h-10 w-10 flex items-center justify-center text-red-600" onClick={onRemove}>
          <MdDeleteOutline />
        </button>
      </div>
    </td>
  </tr>
);

export default Activities;
